import type { ProjectElementNode } from '@deltek/specification-client/core/Models/productListingDetail'

/**
 * Deduplicates PT element nodes by baseElementId, preferring nodes with isInProject: true
 * Each mutation returns the FULL element tree (41 nodes), so we have duplicates
 * We need to keep the version where isInProject: true for each PT
 * @param allUpdatedNodes - Array of PT element nodes from mutations
 * @returns Deduplicated array of PT element nodes
 * @example
 * const nodes = [
 *   { baseElementId: 'base1', id: 'node1', isInProject: false },
 *   { baseElementId: 'base1', id: 'node2', isInProject: true },
 *   { baseElementId: 'base2', id: 'node3', isInProject: false }
 * ]
 * deduplicateNodesByBaseElementId(nodes)
 * // Returns [
 *   { baseElementId: 'base1', id: 'node2', isInProject: true },
 *   { baseElementId: 'base2', id: 'node3', isInProject: false }
 * ]
 */
export const deduplicateNodesByBaseElementId = (
    allUpdatedNodes: ProjectElementNode[]
): ProjectElementNode[] => {
    const nodeMap = new Map<string, ProjectElementNode>()

    allUpdatedNodes.forEach((node) => {
        const key = node.baseElementId || node.id
        const existing = nodeMap.get(key)

        // Keep this node if:
        // 1. No existing node for this baseElementId
        // 2. This node has isInProject: true and existing doesn't
        if (!existing || (node.isInProject && !existing.isInProject)) {
            nodeMap.set(key, node)
        }
    })

    return Array.from(nodeMap.values())
}

/**
 * Updates existing nodes in a list with data from updated PT nodes.
 * Matches nodes by baseElementId and elementType, optionally by id.
 *
 * @param currentNodes - Current list of nodes to update
 * @param updatedNodes - Updated PT nodes from mutation response
 * @param options - Configuration options
 * @param options.matchById - If true, also matches by node.id (stricter matching for category states)
 * @returns Array with updated nodes, preserving local UI state
 *
 * @example
 * const currentNodes = [{ baseElementId: 'base1', elementType: 'PRODUCTTYPE', isInProject: false }]
 * const updatedNodes = [{ baseElementId: 'base1', elementType: 'PRODUCTTYPE', isInProject: true }]
 * updateExistingPTNodes(currentNodes, updatedNodes)
 * // Returns [{ baseElementId: 'base1', elementType: 'PRODUCTTYPE', isInProject: true }]
 */
export const updateExistingPTNodes = <T extends ProjectElementNode>(
    currentNodes: T[],
    updatedNodes: ProjectElementNode[],
    options: { matchById?: boolean } = {}
): T[] => {
    const { matchById = false } = options

    return currentNodes.map((node) => {
        // Find matching PT node from mutation response
        const updatedNode = updatedNodes.find((updated) => {
            const baseMatch =
                updated.baseElementId === node.baseElementId &&
                updated.elementType === 'PRODUCTTYPE'

            // If matchById is true, also check id equality
            return matchById ? baseMatch && updated.id === node.id : baseMatch
        })

        // If found, merge with updated node (preserve local UI state, override with server values)
        if (updatedNode) {
            return {
                ...node, // Keep any local UI state (like selection, expansion)
                ...updatedNode, // Override with all server values from mutation
            } as T
        }

        return node
    })
}

/**
 * Finds new PT nodes that weren't in the existing list.
 * Useful when "Added To Project" filter is ON and newly activated PTs need to be added.
 *
 * @param currentNodes - Current list of nodes
 * @param updatedNodes - Updated PT nodes from mutation response
 * @returns Array of new PT nodes not in current list
 *
 * @example
 * const currentNodes = [{ baseElementId: 'base1', elementType: 'PRODUCTTYPE' }]
 * const updatedNodes = [
 *   { baseElementId: 'base1', elementType: 'PRODUCTTYPE' },
 *   { baseElementId: 'base2', elementType: 'PRODUCTTYPE' }
 * ]
 * findNewPTNodes(currentNodes, updatedNodes)
 * // Returns [{ baseElementId: 'base2', elementType: 'PRODUCTTYPE' }]
 */
export const findNewPTNodes = (
    currentNodes: ProjectElementNode[],
    updatedNodes: ProjectElementNode[]
): ProjectElementNode[] => {
    return updatedNodes.filter(
        (updatedNode) =>
            updatedNode.elementType === 'PRODUCTTYPE' &&
            !currentNodes.some(
                (existingNode) =>
                    existingNode.baseElementId === updatedNode.baseElementId
            )
    )
}

/**
 * Inserts new PT nodes into a list right after their parent family (and after any existing siblings).
 * This ensures the tree-building algorithm (which uses positional order + nesting level) places
 * each PT as a child of the correct PRODUCTFAMILY.
 *
 * @param nodeList - Current list of nodes
 * @param newPTNodes - New PT nodes to insert
 * @returns Updated list with PTs inserted at the correct positions
 *
 * @example
 * const nodeList = [
 *   { id: 'family1', elementType: 'PRODUCTFAMILY', parentNodeId: null },
 *   { id: 'family2', elementType: 'PRODUCTFAMILY', parentNodeId: null },
 * ]
 * const newPTs = [{ id: 'pt1', elementType: 'PRODUCTTYPE', parentNodeId: 'family1' }]
 * insertPTNodesAfterParent(nodeList, newPTs)
 * // Returns [family1, pt1, family2]  — pt1 is right after its parent family1
 */
export const insertPTNodesAfterParent = <T extends ProjectElementNode>(
    nodeList: T[],
    newPTNodes: ProjectElementNode[]
): T[] => {
    let result = [...nodeList]

    for (const pt of newPTNodes) {
        const parentIndex = result.findIndex((n) => n.id === pt.parentNodeId)

        if (parentIndex >= 0) {
            // Find insertion point: after all existing sibling PTs (same parentNodeId)
            let insertIndex = parentIndex + 1
            while (
                insertIndex < result.length &&
                result[insertIndex].parentNodeId === pt.parentNodeId
            ) {
                insertIndex++
            }
            result = [
                ...result.slice(0, insertIndex),
                (pt as unknown) as T,
                ...result.slice(insertIndex),
            ]
        }
    }

    return result
}

/**
 * Updates parent family nodes to mark them as having children.
 * Ensures the tree shows expand arrow for families with newly added PTs.
 *
 * @param nodeList - List of nodes to update (modified in-place via array indexing)
 * @param newPTNodes - Newly added PT nodes
 * @returns Updated list with parent families marked as hasChildren: true
 *
 * @example
 * const nodeList = [
 *   { id: 'family1', elementType: 'PRODUCTFAMILY', hasChildren: false },
 *   { id: 'pt1', elementType: 'PRODUCTTYPE', parentNodeId: 'family1' }
 * ]
 * const newPTs = [{ id: 'pt2', elementType: 'PRODUCTTYPE', parentNodeId: 'family1' }]
 * updateParentFamilyNodes(nodeList, newPTs)
 * // Returns [
 * //   { id: 'family1', elementType: 'PRODUCTFAMILY', hasChildren: true },
 * //   { id: 'pt1', elementType: 'PRODUCTTYPE', parentNodeId: 'family1' }
 * // ]
 */
export const updateParentFamilyNodes = <T extends ProjectElementNode>(
    nodeList: T[],
    newPTNodes: ProjectElementNode[]
): T[] => {
    if (newPTNodes.length === 0) {
        return nodeList
    }

    // Extract unique parent family IDs from new PTs
    const parentFamilyIds = new Set(
        newPTNodes
            .map((pt) => pt.parentNodeId)
            .filter((id): id is string => !!id)
    )

    // Update each parent family to mark hasChildren: true
    parentFamilyIds.forEach((familyId) => {
        const familyIndex = nodeList.findIndex((node) => node.id === familyId)
        if (familyIndex !== -1) {
            // Create NEW object instead of mutating to ensure React detects the change
            nodeList[familyIndex] = {
                ...nodeList[familyIndex],
                hasChildren: true,
            }
        }
    })

    return nodeList
}
